import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';

import {
  apiAdminGetQuickLinks,
  apiAdminCreateQuickLink,
  apiAdminUpdateQuickLink,
  apiAdminDeleteQuickLink,
  apiAdminReorderQuickLinks,
} from 'api';

interface QuickLink {
  id: number;
  title: string;
  url: string;
  order: number;
  is_active: boolean;
  open_in_new_tab: boolean;
}

const TABLE_HEADERS = ['Order', 'Title', 'URL', 'New Tab', 'Active', 'Actions'];

const emptyForm = () => ({
  title: '',
  url: '',
  order: 0,
  is_active: true,
  open_in_new_tab: true,
});

const QuickLinksView: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  const [editing, setEditing] = useState<QuickLink | null>(null);
  const [form, setForm] = useState(emptyForm());

  const {
    data: links = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<QuickLink[]>({
    queryKey: ['admin-quick-links'],
    queryFn: () =>
      apiAdminGetQuickLinks().then((r: any) =>
        Array.isArray(r) ? r : (r?.data ?? []),
      ),
  });

  const inv = () => qc.invalidateQueries({ queryKey: ['admin-quick-links'] });

  const createMutation = useMutation({
    mutationFn: (data: object) => apiAdminCreateQuickLink(data),
    onSuccess: () => {
      toast({ title: 'Quick link created', status: 'success' });
      inv();
      onClose();
    },
    onError: () =>
      toast({ title: 'Failed to create quick link', status: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) =>
      apiAdminUpdateQuickLink(id, data),
    onSuccess: () => {
      toast({ title: 'Quick link updated', status: 'success' });
      inv();
      onClose();
    },
    onError: () =>
      toast({ title: 'Failed to update quick link', status: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      apiAdminUpdateQuickLink(id, { is_active }),
    onSuccess: inv,
    onError: () => toast({ title: 'Failed to update', status: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiAdminDeleteQuickLink(id),
    onSuccess: () => {
      toast({ title: 'Quick link removed', status: 'success' });
      inv();
    },
    onError: () => toast({ title: 'Failed to remove', status: 'error' }),
  });

  const reorderMutation = useMutation({
    mutationFn: (items: { id: number; order: number }[]) =>
      apiAdminReorderQuickLinks(items),
    onSuccess: inv,
    onError: () => toast({ title: 'Failed to reorder', status: 'error' }),
  });

  const handleReorder = (id: number, direction: 'up' | 'down') => {
    const s = [...links].sort((a, b) => a.order - b.order);
    const idx = s.findIndex((l) => l.id === id);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === s.length - 1) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    // Swap order values and send as a single atomic batch
    reorderMutation.mutate([
      { id: s[idx].id, order: s[swap].order },
      { id: s[swap].id, order: s[idx].order },
    ]);
  };

  const handleSubmit = () => {
    if (!form.title.trim()) {
      toast({ title: 'Title is required', status: 'warning' });
      return;
    }
    if (!form.url.trim()) {
      toast({ title: 'URL is required', status: 'warning' });
      return;
    }
    const payload = {
      title: form.title,
      url: form.url,
      order: form.order,
      is_active: form.is_active,
      open_in_new_tab: form.open_in_new_tab,
    };
    editing
      ? updateMutation.mutate({ id: editing.id, data: payload })
      : createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    onOpen();
  };
  const openEdit = (l: QuickLink) => {
    setEditing(l);
    setForm({
      title: l.title,
      url: l.url,
      order: l.order,
      is_active: l.is_active,
      open_in_new_tab: l.open_in_new_tab,
    });
    onOpen();
  };
  const isBusy = createMutation.isPending || updateMutation.isPending;
  const sorted = [...links].sort((a, b) => a.order - b.order);

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Quick Links
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh quick links" hasArrow>
            <IconButton
              aria-label="Refresh quick links"
              icon={<RepeatIcon />}
              onClick={() => refetch()}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={openCreate}
          >
            New Quick Link
          </Button>
        </Flex>
      </Flex>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading quick links...
                </Flex>
              </Td>
            </Tr>
          )}

          {sorted.map((l, idx) => (
            <Tr
              key={l.id}
              cursor="pointer"
              opacity={l.is_active ? 1 : 0.5}
              _hover={{ bg: hoverBg }}
              onClick={() => openEdit(l)}
            >
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Move up"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronUpIcon boxSize={4} />}
                    isDisabled={idx === 0}
                    onClick={() => handleReorder(l.id, 'up')}
                  />
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {l.order}
                  </Text>
                  <IconButton
                    aria-label="Move down"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronDownIcon boxSize={4} />}
                    isDisabled={idx === sorted.length - 1}
                    onClick={() => handleReorder(l.id, 'down')}
                  />
                </HStack>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm" fontWeight="700">
                  {l.title}
                </Text>
              </Td>
              <Td>
                <Text
                  color={mutedColor}
                  fontSize="sm"
                  maxW="260px"
                  noOfLines={1}
                  title={l.url}
                >
                  {l.url}
                </Text>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {l.open_in_new_tab ? 'Yes' : 'No'}
                </Text>
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <Switch
                  size="sm"
                  isChecked={l.is_active}
                  onChange={(e) =>
                    toggleMutation.mutate({
                      id: l.id,
                      is_active: e.target.checked,
                    })
                  }
                />
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <HStack spacing={1}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(l)}
                  >
                    Edit
                  </Button>
                  <Tooltip label="Remove" hasArrow>
                    <IconButton
                      aria-label="Remove quick link"
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      icon={<DeleteIcon />}
                      onClick={() => {
                        if (window.confirm(`Remove "${l.title}"?`))
                          deleteMutation.mutate(l.id);
                      }}
                    />
                  </Tooltip>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!isLoading && sorted.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px" color={mutedColor}>
          No quick links yet. Click "New Quick Link" to create one.
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editing ? 'Edit Quick Link' : 'Add Quick Link'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Title</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.title}
                  placeholder="e.g. Underwriting Guidelines"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">URL</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.url}
                  placeholder="https://..."
                  onChange={(e) =>
                    setForm((f) => ({ ...f, url: e.target.value }))
                  }
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel fontSize="sm" mb={0}>
                  Open in new tab
                </FormLabel>
                <Switch
                  isChecked={form.open_in_new_tab}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      open_in_new_tab: e.target.checked,
                    }))
                  }
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel fontSize="sm" mb={0}>
                  Active
                </FormLabel>
                <Switch
                  isChecked={form.is_active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, is_active: e.target.checked }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={isBusy} onClick={handleSubmit}>
              {editing ? 'Save changes' : 'Create link'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default QuickLinksView;
